import React from 'react';
import styled from 'styled-components';
import { useTheme, useSidebar, useAuth } from '@/context';
import { Avatar, TenantBadge, TenantSwitcher } from '@/components/common';
import { GlobalSearch } from './GlobalSearch';

const HeaderWrapper = styled.header<{ $sidebarCollapsed: boolean }>`
  position: fixed;
  top: 0;
  left: ${({ $sidebarCollapsed }) => $sidebarCollapsed ? '80px' : '280px'};
  right: 0;
  height: 72px;
  background: ${({ theme }) => theme.glass.background};
  backdrop-filter: ${({ theme }) => theme.glass.backdropBlur};
  -webkit-backdrop-filter: ${({ theme }) => theme.glass.backdropBlur};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing.xl};
  z-index: ${({ theme }) => theme.zIndex.sticky};
  transition: left ${({ theme }) => theme.transitions.normal};
  
  @media (max-width: 1024px) {
    left: 0;
    padding: 0 ${({ theme }) => theme.spacing.md};
  }
  
  @media (max-width: 480px) {
    height: 60px;
    padding: 0 ${({ theme }) => theme.spacing.sm};
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex: 1;
  
  @media (max-width: 480px) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.text};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }
  
  &:active {
    background: ${({ theme }) => theme.colors.primaryLight};
  }
  
  @media (max-width: 1024px) {
    display: flex;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const SearchWrapper = styled.div`
  width: 320px;
  
  @media (max-width: 1024px) {
    width: 200px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: 480px) {
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const IconButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.05);
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const NotificationDot = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryGradient};
  border: 2px solid ${({ theme }) => theme.colors.surface};
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.2); opacity: 0.8; }
  }
`;

const UserSection = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  padding-right: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-left: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.glassGradient};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    border-color: ${({ theme }) => theme.colors.borderGold};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.xs};
    margin-left: ${({ theme }) => theme.spacing.xs};
    gap: 0;
  }
`;

const UserMenuWrapper = styled.div`
  position: relative;
`;

const UserMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  padding: ${({ theme }) => theme.spacing.xs};
  z-index: ${({ theme }) => theme.zIndex.dropdown};
`;

const UserMenuItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }
`;

const UserInfo = styled.div`
  text-align: right;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const UserRole = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const ThemeToggle = styled(IconButton)`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryLight};
  }
  
  @media (max-width: 480px) {
    display: none;
  }
`;

export const Header: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { isCollapsed, toggleMobile } = useSidebar();
  const { user, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click + Escape key.
  React.useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Use email prefix as display name until Phase 2 Staff profiles land.
  const getUserDisplayName = () => {
    if (!user) return 'Guest';
    return user.email?.split('@')[0] || 'User';
  };

  const getUserRole = () => {
    if (!user?.role) return '';
    return user.role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
  };

  return (
    <HeaderWrapper $sidebarCollapsed={isCollapsed}>
      <LeftSection>
        <MobileMenuButton onClick={toggleMobile}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </MobileMenuButton>
        <SearchWrapper>
          <GlobalSearch />
        </SearchWrapper>
        <TenantBadge />
        <TenantSwitcher />
      </LeftSection>

      <RightSection>
        <ThemeToggle onClick={toggleTheme} title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
          {isDark ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </ThemeToggle>

        <IconButton title="Notifications">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          <NotificationDot />
        </IconButton>

        <UserMenuWrapper ref={menuRef}>
          <UserSection
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid="user-menu-trigger"
            title="Account menu"
          >
            <UserInfo>
              <UserName>{getUserDisplayName()}</UserName>
              <UserRole>{getUserRole()}</UserRole>
            </UserInfo>
            <Avatar
              src={undefined}
              firstName={getUserDisplayName().charAt(0).toUpperCase()}
              lastName=""
              size="sm"
            />
          </UserSection>
          {menuOpen && (
            <UserMenu role="menu" data-testid="user-menu">
              <UserMenuItem
                type="button"
                role="menuitem"
                onClick={handleSignOut}
                data-testid="sign-out-button"
              >
                Sign out
              </UserMenuItem>
            </UserMenu>
          )}
        </UserMenuWrapper>
      </RightSection>
    </HeaderWrapper>
  );
};
